import React, { useCallback, useMemo, useState, useRef, createElement } from 'react';
import { Search, MessageSquare, AlertTriangle, CheckCircle2, Clock, Send, User, Paperclip, Plus, Star, Check, ShieldAlert, CreditCard, Zap, Download, AlignJustify, Pin, Lock, FileText, Shield, History, Info, X, CheckCheck, Tag, ChevronDown, ChevronRight, RefreshCw, Mail, Phone, AlertOctagon, ArrowLeft, ArrowUp } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface Ticket {
  id: string;
  subject: string;
  user: string;
  priority: string;
  status: string;
  date: string;
}
interface AdminMessagingProps {
  tickets: Ticket[];
  setTickets: React.Dispatch<React.SetStateAction<Ticket[]>>;
  success: (msg: string) => void;
}
interface ChatMessage {
  id: string;
  sender: 'user' | 'admin' | 'system' | 'astrologer' | 'note';
  text: string;
  time: string;
  isRead?: boolean;
  attachments?: string[];
}
interface ExtendedTicket extends Ticket {
  avatar: string;
  type: 'support' | 'complaint' | 'refund' | 'tech' | 'ai' | 'finance';
  unread: number;
  isPinned: boolean;
  hasAttachment: boolean;
  slaStatus: 'ok' | 'warning' | 'breached';
  slaTime: string;
  riskLevel: 'low' | 'medium' | 'high';
  assignee: string | null;
  lastMessage: string;
  messages: ChatMessage[];
  isEscalated: boolean;
  isBlocked: boolean;
  hasRefund: boolean;
  userProfile: {
    email: string;
    phone: string;
    regDate: string;
    ltv: string;
    totalOrders: number;
    activeSubscription: string | null;
  };
}
const generateMockMessages = (ticket: Ticket): ChatMessage[] => {
  const isComplaint = ticket.subject.toLowerCase().includes('жалоб');
  const isRefund = ticket.subject.toLowerCase().includes('возврат');
  const messages: ChatMessage[] = [{
    id: 'm1',
    sender: 'system',
    text: `Тикет создан. Категория: ${isComplaint ? 'Жалоба' : isRefund ? 'Финансы' : 'Поддержка'}`,
    time: '10:00'
  }, {
    id: 'm2',
    sender: 'user',
    text: `Здравствуйте! У меня проблема: ${ticket.subject.toLowerCase()}. Помогите разобраться, пожалуйста.`,
    time: '10:05',
    attachments: ticket.id.endsWith('2') ? ['screenshot.png'] : undefined
  }];
  if (ticket.status === 'in_progress' || ticket.status === 'closed') {
    messages.push({
      id: 'm3',
      sender: 'admin',
      text: 'Добрый день! Взял ваш вопрос в работу. Сейчас всё проверим.',
      time: '10:15',
      isRead: true
    });
  }
  if (isComplaint) {
    messages.push({
      id: 'm4',
      sender: 'note',
      text: 'Внутренняя заметка: проверить историю заказов клиента, возможно повторная жалоба.',
      time: '10:20'
    });
  }
  if (ticket.status === 'closed') {
    messages.push({
      id: 'm5',
      sender: 'admin',
      text: 'Проблема решена. Если возникнут новые вопросы — обращайтесь!',
      time: '11:00',
      isRead: true
    });
    messages.push({
      id: 'm6',
      sender: 'system',
      text: 'Тикет закрыт администратором',
      time: '11:01'
    });
  }
  return messages;
};
const enrichTicket = (t: Ticket, index: number): ExtendedTicket => {
  const isComplaint = t.subject.toLowerCase().includes('жалоб');
  const isRefund = t.subject.toLowerCase().includes('возврат');
  const isTech = t.subject.toLowerCase().includes('ошибк') || t.subject.toLowerCase().includes('проблем');
  let type: ExtendedTicket['type'] = 'support';
  if (isComplaint) type = 'complaint';else if (isRefund) type = 'refund';else if (isTech) type = 'tech';else if (index % 5 === 0) type = 'ai';
  const slaMinutes = Math.floor(Math.random() * 180);
  let slaStatus: ExtendedTicket['slaStatus'] = 'ok';
  if (slaMinutes > 120) slaStatus = 'breached';else if (slaMinutes > 60) slaStatus = 'warning';
  return {
    ...t,
    avatar: t.user.substring(0, 2).toUpperCase(),
    type,
    unread: t.status === 'open' ? Math.floor(Math.random() * 3) + 1 : 0,
    isPinned: index === 0,
    hasAttachment: index % 3 === 0,
    slaStatus,
    slaTime: `${Math.floor(slaMinutes / 60)}ч ${slaMinutes % 60}м`,
    riskLevel: isComplaint ? 'high' : isRefund ? 'medium' : 'low',
    assignee: t.status !== 'open' ? 'Алексей (Admin)' : null,
    lastMessage: t.status === 'closed' ? 'Проблема решена.' : `У меня проблема: ${t.subject.toLowerCase()}...`,
    messages: generateMockMessages(t),
    isEscalated: false,
    isBlocked: false,
    hasRefund: false,
    userProfile: {
      email: `user${index}@example.com`,
      phone: '+7 (999) 000-00-00',
      regDate: '12.05.2023',
      ltv: `${(index * 1500 + 3000).toLocaleString('ru-RU')} \u20BD`,
      totalOrders: index % 5 + 1,
      activeSubscription: index % 2 === 0 ? 'Pro' : null
    }
  };
};
const quickTemplates = [{
  label: 'Приветствие',
  text: 'Здравствуйте! Спасибо за обращение. Сейчас разберёмся с вашим вопросом.'
}, {
  label: 'Запрос деталей',
  text: 'Пожалуйста, уточните детали: номер заказа и дату оплаты.'
}, {
  label: 'Решено',
  text: 'Проблема решена. Если есть ещё вопросы — пишите!'
}, {
  label: 'Ожидайте',
  text: 'Ваш запрос передан специалисту. Ответим в течение 1 часа.'
}, {
  label: 'Возврат',
  text: 'Возврат оформлен. Средства поступят в течение 3-5 рабочих дней.'
}, {
  label: 'Эскалация',
  text: 'Ваш вопрос передан старшему специалисту для детального рассмотрения.'
}];
const categoryTabs = [{
  id: 'all',
  label: 'Все'
}, {
  id: 'support',
  label: 'Поддержка'
}, {
  id: 'complaints',
  label: 'Жалобы'
}, {
  id: 'refunds',
  label: 'Возвраты'
}, {
  id: 'tech',
  label: 'Технические'
}, {
  id: 'ai',
  label: 'ИИ-отчёты'
}];
const ADMIN = 'Вы (Admin)';
const timeNow = () => new Date().toLocaleTimeString([], {
  hour: '2-digit',
  minute: '2-digit'
});
export function AdminMessaging({
  tickets,
  setTickets,
  success
}: AdminMessagingProps) {
  const [activeTab, setActiveTab] = useState('all');
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<Set<string>>(new Set());
  const [metricFilter, setMetricFilter] = useState<string | null>(null);
  const [pinnedTickets, setPinnedTickets] = useState<Set<string>>(new Set(['TKT-200']));
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [extraMessages, setExtraMessages] = useState<Record<string, ChatMessage[]>>({});
  const [ticketFlags, setTicketFlags] = useState<Record<string, {
    isEscalated?: boolean;
    isBlocked?: boolean;
    hasRefund?: boolean;
  }>>({});
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketUser, setNewTicketUser] = useState('');
  const [newTicketPriority, setNewTicketPriority] = useState('medium');
  const [confirmAction, setConfirmAction] = useState<{
    label: string;
    action: () => void;
  } | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    type: 'all'
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  // Enrich tickets
  const extendedTickets = useMemo(() => tickets.map((t, i) => {
    const et = enrichTicket(t, i);
    const flags = ticketFlags[t.id];
    if (flags) {
      if (flags.isEscalated) et.isEscalated = true;
      if (flags.isBlocked) et.isBlocked = true;
      if (flags.hasRefund) et.hasRefund = true;
    }
    return et;
  }), [tickets, ticketFlags]);
  // Metrics
  const metrics = useMemo(() => ({
    total: extendedTickets.length,
    open: extendedTickets.filter(t => t.status === 'open').length,
    inProgress: extendedTickets.filter(t => t.status === 'in_progress').length,
    overdue: extendedTickets.filter(t => t.slaStatus === 'breached' && t.status !== 'closed').length,
    complaints: extendedTickets.filter(t => t.type === 'complaint').length,
    refunds: extendedTickets.filter(t => t.type === 'refund').length,
    noReply: extendedTickets.filter(t => t.status === 'open' && t.unread > 0).length,
    closed: extendedTickets.filter(t => t.status === 'closed').length
  }), [extendedTickets]);
  // Filtering
  const filteredTickets = useMemo(() => {
    let result = [...extendedTickets];
    if (metricFilter === 'open') result = result.filter(t => t.status === 'open');else if (metricFilter === 'inProgress') result = result.filter(t => t.status === 'in_progress');else if (metricFilter === 'overdue') result = result.filter(t => t.slaStatus === 'breached' && t.status !== 'closed');else if (metricFilter === 'complaints') result = result.filter(t => t.type === 'complaint');else if (metricFilter === 'refunds') result = result.filter(t => t.type === 'refund');else if (metricFilter === 'noReply') result = result.filter(t => t.status === 'open' && t.unread > 0);else if (metricFilter === 'closed') result = result.filter(t => t.status === 'closed');
    if (activeTab === 'support') result = result.filter(t => t.type === 'support');else if (activeTab === 'complaints') result = result.filter(t => t.type === 'complaint');else if (activeTab === 'refunds') result = result.filter(t => t.type === 'refund');else if (activeTab === 'tech') result = result.filter(t => t.type === 'tech');else if (activeTab === 'ai') result = result.filter(t => t.type === 'ai');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.subject.toLowerCase().includes(q) || t.user.toLowerCase().includes(q) || t.id.toLowerCase().includes(q));
    }
    if (filters.status !== 'all') result = result.filter(t => t.status === filters.status);
    if (filters.priority !== 'all') result = result.filter(t => t.priority === filters.priority);
    if (filters.type !== 'all') result = result.filter(t => t.type === filters.type);
    return result.sort((a, b) => {
      if (pinnedTickets.has(a.id) && !pinnedTickets.has(b.id)) return -1;
      if (!pinnedTickets.has(a.id) && pinnedTickets.has(b.id)) return 1;
      if (a.unread > 0 && b.unread === 0) return -1;
      if (a.unread === 0 && b.unread > 0) return 1;
      return 0;
    });
  }, [extendedTickets, activeTab, searchQuery, filters, metricFilter, pinnedTickets]);
  const activeTicket = useMemo(() => extendedTickets.find(t => t.id === activeTicketId), [extendedTickets, activeTicketId]);
  const allMessages = activeTicket ? [...activeTicket.messages, ...(extraMessages[activeTicket.id] || [])] : [];
  // --- System message helper ---
  const addSystemMessage = useCallback((ticketId: string, text: string) => {
    setExtraMessages(prev => ({
      ...prev,
      [ticketId]: [...(prev[ticketId] || []), {
        id: `sys-${Date.now()}`,
        sender: 'system' as const,
        text,
        time: timeNow()
      }]
    }));
  }, []);
  // --- Handlers ---
  const handleSendMessage = useCallback(() => {
    if (!messageText.trim() || !activeTicketId) return;
    const newMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      sender: isInternalNote ? 'note' : 'admin',
      text: messageText,
      time: timeNow(),
      isRead: false
    };
    setExtraMessages(prev => ({
      ...prev,
      [activeTicketId]: [...(prev[activeTicketId] || []), newMsg]
    }));
    // Auto-set to in_progress if open
    setTickets(prev => prev.map(t => t.id === activeTicketId && t.status === 'open' ? {
      ...t,
      status: 'in_progress'
    } : t));
    success(isInternalNote ? 'Заметка добавлена' : 'Сообщение отправлено');
    setMessageText('');
    setIsInternalNote(false);
    setTimeout(() => chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    }), 100);
  }, [messageText, activeTicketId, isInternalNote, setTickets, success]);
  const handleCloseTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'closed'
    } : t));
    addSystemMessage(id, 'Тикет закрыт администратором');
    success('Тикет закрыт');
  }, [setTickets, addSystemMessage, success]);
  const handleReopenTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'open'
    } : t));
    addSystemMessage(id, 'Тикет переоткрыт администратором');
    success('Тикет переоткрыт');
  }, [setTickets, addSystemMessage, success]);
  const handleTakeTicket = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'in_progress'
    } : t));
    addSystemMessage(id, `Тикет назначен на ${ADMIN}`);
    success('Назначено на вас');
  }, [setTickets, addSystemMessage, success]);
  const handleEscalate = useCallback((id: string) => {
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      priority: 'high',
      status: t.status === 'open' ? 'in_progress' : t.status
    } : t));
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isEscalated: true
      }
    }));
    addSystemMessage(id, 'Тикет эскалирован руководству. Приоритет повышен до высокого.');
    success('Тикет эскалирован');
  }, [setTickets, addSystemMessage, success]);
  const handleRefund = useCallback((id: string) => {
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        hasRefund: true
      }
    }));
    addSystemMessage(id, 'Возврат средств оформлен. Средства поступят в течение 3-5 рабочих дней.');
    setExtraMessages(prev => ({
      ...prev,
      [id]: [...(prev[id] || []), {
        id: `adm-${Date.now()}`,
        sender: 'admin' as const,
        text: 'Возврат оформлен. Средства поступят в течение 3-5 рабочих дней.',
        time: timeNow(),
        isRead: false
      }]
    }));
    success('Возврат оформлен');
  }, [addSystemMessage, success]);
  const handleBlockUser = useCallback((id: string, userName: string) => {
    setTicketFlags(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        isBlocked: true
      }
    }));
    addSystemMessage(id, `Пользователь ${userName} заблокирован администратором`);
    setTickets(prev => prev.map(t => t.id === id ? {
      ...t,
      status: 'closed'
    } : t));
    success(`Пользователь ${userName} заблокирован`);
    setConfirmAction(null);
  }, [setTickets, addSystemMessage, success]);
  const handleCreateTicket = useCallback(() => {
    if (!newTicketSubject.trim()) return;
    const newId = `TKT-${300 + tickets.length}`;
    setTickets(prev => [{
      id: newId,
      subject: newTicketSubject,
      user: newTicketUser || 'Администратор',
      priority: newTicketPriority,
      status: 'open',
      date: 'Сегодня'
    }, ...prev]);
    success(`Тикет ${newId} создан`);
    setNewTicketSubject('');
    setNewTicketUser('');
    setNewTicketPriority('medium');
    setShowNewTicketForm(false);
    setActiveTicketId(newId);
  }, [newTicketSubject, newTicketUser, newTicketPriority, tickets.length, setTickets, success]);
  const handleExport = useCallback(() => {
    const csvRows = ['ID,Пользователь,Тема,Статус,Приоритет,Дата'];
    filteredTickets.forEach(t => csvRows.push(`${t.id},${t.user},${t.subject},${t.status},${t.priority},${t.date}`));
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets_export_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    success(`Экспортировано ${filteredTickets.length} тикетов`);
  }, [filteredTickets, success]);
  const togglePin = useCallback((id: string) => {
    setPinnedTickets(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        success('Откреплён');
      } else {
        next.add(id);
        success('Закреплён');
      }
      return next;
    });
  }, [success]);
  const toggleTicketSelection = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTickets(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);else n.add(id);
      return n;
    });
  };
  const handleMassAction = useCallback((action: string) => {
    if (selectedTickets.size === 0) return;
    const ids = Array.from(selectedTickets);
    if (action === 'close') {
      setTickets(prev => prev.map(t => ids.includes(t.id) ? {
        ...t,
        status: 'closed'
      } : t));
      ids.forEach(id => addSystemMessage(id, 'Тикет закрыт (массовое действие)'));
    } else if (action === 'assign') {
      setTickets(prev => prev.map(t => ids.includes(t.id) && t.status === 'open' ? {
        ...t,
        status: 'in_progress'
      } : t));
      ids.forEach(id => addSystemMessage(id, `Назначен на ${ADMIN}`));
    }
    success(`${action === 'close' ? 'Закрыто' : 'Назначено'}: ${ids.length} тикетов`);
    setSelectedTickets(new Set());
  }, [selectedTickets, setTickets, addSystemMessage, success]);
  const handleFileAttach = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeTicketId) return;
    setExtraMessages(prev => ({
      ...prev,
      [activeTicketId]: [...(prev[activeTicketId] || []), {
        id: `msg-${Date.now()}`,
        sender: 'admin' as const,
        text: `\u{1F4CE} ${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
        time: timeNow(),
        attachments: [file.name]
      }]
    }));
    e.target.value = '';
    success('Файл прикреплён');
  }, [activeTicketId, success]);
  const toggleSection = (s: string) => {
    setCollapsedSections(prev => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s);else n.add(s);
      return n;
    });
  };
  // --- Render helpers ---
  const getTypeIcon = (type: ExtendedTicket['type']) => {
    switch (type) {
      case 'complaint':
        return <ShieldAlert className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" data-id="element-5732" />;
      case 'refund':
        return <CreditCard className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-5733" />;
      case 'tech':
        return <Zap className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" data-id="element-5734" />;
      case 'ai':
        return <Star className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" data-id="element-5735" />;
      case 'finance':
        return <CreditCard className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" data-id="element-5736" />;
      default:
        return <MessageSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5737" />;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="error" size="sm" data-id="element-5738">
            Новый
          </Badge>;
      case 'in_progress':
        return <Badge variant="warning" size="sm" data-id="element-5739">
            В работе
          </Badge>;
      case 'closed':
        return <Badge variant="neutral" size="sm" data-id="element-5740">
            Закрыт
          </Badge>;
      default:
        return <Badge variant="neutral" size="sm" data-id="element-5741">
            {status}
          </Badge>;
    }
  };
  const getSlaColor = (status: string) => {
    if (status === 'breached') return 'text-rose-600 bg-rose-50 border-rose-200';
    if (status === 'warning') return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  };
  const getRiskBadge = (level: string) => {
    if (level === 'high') return <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-5742">
          Высокий
        </span>;
    if (level === 'medium') return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5743">
          Средний
        </span>;
    return <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded" data-id="element-5744">
        Низкий
      </span>;
  };
  const isTerminal = (s: string) => s === 'closed';
  const isMobileChat = !!activeTicketId;
  const metricCards = [{
    key: null,
    label: 'Всего',
    value: metrics.total,
    icon: <MessageSquare className="w-4 h-4" data-id="element-5745" />,
    color: 'text-slate-600 bg-slate-50'
  }, {
    key: 'open',
    label: 'Открытые',
    value: metrics.open,
    icon: <AlertTriangle className="w-4 h-4" data-id="element-5746" />,
    color: 'text-rose-600 bg-rose-50'
  }, {
    key: 'inProgress',
    label: 'В работе',
    value: metrics.inProgress,
    icon: <Clock className="w-4 h-4" data-id="element-5747" />,
    color: 'text-blue-600 bg-blue-50'
  }, {
    key: 'overdue',
    label: 'Просрочено',
    value: metrics.overdue,
    icon: <AlertOctagon className="w-4 h-4" data-id="element-5748" />,
    color: 'text-rose-600 bg-rose-50'
  }, {
    key: 'noReply',
    label: 'Без ответа',
    value: metrics.noReply,
    icon: <Clock className="w-4 h-4" data-id="element-5749" />,
    color: 'text-amber-600 bg-amber-50'
  }, {
    key: 'complaints',
    label: 'Жалобы',
    value: metrics.complaints,
    icon: <ShieldAlert className="w-4 h-4" data-id="element-5750" />,
    color: 'text-orange-600 bg-orange-50'
  }, {
    key: 'refunds',
    label: 'Возвраты',
    value: metrics.refunds,
    icon: <CreditCard className="w-4 h-4" data-id="element-5751" />,
    color: 'text-purple-600 bg-purple-50'
  }, {
    key: 'closed',
    label: 'Закрыто',
    value: metrics.closed,
    icon: <CheckCircle2 className="w-4 h-4" data-id="element-5752" />,
    color: 'text-emerald-600 bg-emerald-50'
  }];
  return <div className="space-y-3 h-[calc(100vh-10rem)] min-h-[600px] flex flex-col" data-id="element-5753">
      {/* Confirm dialog */}
      {confirmAction && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setConfirmAction(null)} data-id="element-5754">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-5755">
            <h3 className="text-base font-bold text-slate-900 mb-2" data-id="element-5756">
              Подтверждение
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-5757">
              Вы уверены: <strong data-id="element-5758">{confirmAction.label}</strong>?
            </p>
            <div className="flex gap-2 justify-end" data-id="element-5759">
              <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} data-id="element-5760">
                Отмена
              </Button>
              <Button variant="primary" size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={confirmAction.action} data-id="element-5761">
                Подтвердить
              </Button>
            </div>
          </div>
        </div>}

      {/* New Ticket Form */}
      {showNewTicketForm && <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowNewTicketForm(false)} data-id="element-5762">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-5763">
            <div className="flex items-center justify-between mb-4" data-id="element-5764">
              <h3 className="text-base font-bold text-slate-900" data-id="element-5765">
                Новый тикет
              </h3>
              <button onClick={() => setShowNewTicketForm(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded" data-id="element-5766">
                <X className="w-4 h-4" data-id="element-5767" />
              </button>
            </div>
            <div className="space-y-3" data-id="element-5768">
              <div data-id="element-5769">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5770">
                  Тема *
                </label>
                <input type="text" value={newTicketSubject} onChange={e => setNewTicketSubject(e.target.value)} placeholder="Опишите проблему..." className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5771" />
              </div>
              <div data-id="element-5772">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5773">
                  Пользователь
                </label>
                <input type="text" value={newTicketUser} onChange={e => setNewTicketUser(e.target.value)} placeholder="Имя пользователя" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5774" />
              </div>
              <div data-id="element-5775">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-5776">
                  Приоритет
                </label>
                <select value={newTicketPriority} onChange={e => setNewTicketPriority(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500" data-id="element-5777">
                  <option value="low" data-id="element-5778">Низкий</option>
                  <option value="medium" data-id="element-5779">Средний</option>
                  <option value="high" data-id="element-5780">Высокий</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end pt-2" data-id="element-5781">
                <Button variant="outline" size="sm" onClick={() => setShowNewTicketForm(false)} data-id="element-5782">
                  Отмена
                </Button>
                <Button variant="primary" size="sm" onClick={handleCreateTicket} disabled={!newTicketSubject.trim()} data-id="element-5783">
                  Создать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0" data-id="element-5784">
        <div data-id="element-5785">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-5786">
            Тикеты / Поддержка
          </h2>
          <p className="text-xs text-slate-500" data-id="element-5787">
            {filteredTickets.length} из {extendedTickets.length} тикетов
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-5788">
          <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-5790" />} onClick={handleExport} data-id="element-5789">
            Экспорт
          </Button>
          <Button variant="outline" size="sm" icon={<RefreshCw className="w-4 h-4" data-id="element-5792" />} onClick={() => {
          setExtraMessages({});
          setTicketFlags({});
          success('Данные обновлены');
        }} data-id="element-5791">
            Обновить
          </Button>
          <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-5794" />} onClick={() => setShowNewTicketForm(true)} data-id="element-5793">
            Новый тикет
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-4 xl:grid-cols-8 gap-2 flex-shrink-0" data-id="element-5795">
        {metricCards.map(m => <button key={m.label} onClick={() => setMetricFilter(metricFilter === m.key ? null : m.key)} className={`flex items-center gap-2 p-2 rounded-xl border transition-all text-left ${metricFilter === m.key ? 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`} data-id="element-5796">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${m.color}`} data-id="element-5797">
              {m.icon}
            </div>
            <div className="min-w-0" data-id="element-5798">
              <div className="text-base font-bold text-slate-900 leading-none" data-id="element-5799">
                {m.value}
              </div>
              <div className="text-[9px] text-slate-500 truncate" data-id="element-5800">
                {m.label}
              </div>
            </div>
          </button>)}
      </div>

      {/* Tabs + Controls */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap" data-id="element-5801">
        <div className="flex gap-1 overflow-x-auto" data-id="element-5802">
          {categoryTabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-5803">
              {tab.label}
            </button>)}
        </div>
        <div className="flex-1" data-id="element-5804" />
        <div className="flex items-center gap-1.5" data-id="element-5805">
          <button onClick={() => setCompactMode(!compactMode)} className={`p-1.5 rounded-lg transition-colors ${compactMode ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} title="Компактный режим" data-id="element-5806">
            <AlignJustify className="w-4 h-4" data-id="element-5807" />
          </button>
          <button onClick={() => setShowFilters(!showFilters)} className={`p-1.5 rounded-lg transition-colors ${showFilters ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} title="Фильтры" data-id="element-5808">
            <Search className="w-4 h-4" data-id="element-5809" />
          </button>
          {selectedTickets.size > 0 && <div className="flex items-center gap-1 ml-2 pl-2 border-l border-slate-200" data-id="element-5810">
              <span className="text-xs text-slate-500" data-id="element-5811">
                {selectedTickets.size}
              </span>
              <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => handleMassAction('assign')} data-id="element-5812">
                Назначить
              </Button>
              <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => handleMassAction('close')} data-id="element-5813">
                Закрыть
              </Button>
              <Button variant="ghost" size="sm" className="text-[10px] h-7 px-2" onClick={() => setSelectedTickets(new Set())} data-id="element-5814">
                Снять
              </Button>
            </div>}
        </div>
      </div>

      {/* Filters */}
      {showFilters && <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl flex-shrink-0 flex-wrap" data-id="element-5815">
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.status} onChange={e => setFilters({
        ...filters,
        status: e.target.value
      })} data-id="element-5816">
            <option value="all" data-id="element-5817">Статус: все</option>
            <option value="open" data-id="element-5818">Открыт</option>
            <option value="in_progress" data-id="element-5819">В работе</option>
            <option value="closed" data-id="element-5820">Закрыт</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.priority} onChange={e => setFilters({
        ...filters,
        priority: e.target.value
      })} data-id="element-5821">
            <option value="all" data-id="element-5822">Приоритет: все</option>
            <option value="high" data-id="element-5823">Высокий</option>
            <option value="medium" data-id="element-5824">Средний</option>
            <option value="low" data-id="element-5825">Низкий</option>
          </select>
          <select className="rounded-lg border border-slate-200 bg-white text-xs py-1.5 px-2" value={filters.type} onChange={e => setFilters({
        ...filters,
        type: e.target.value
      })} data-id="element-5826">
            <option value="all" data-id="element-5827">Тип: все</option>
            <option value="support" data-id="element-5828">Поддержка</option>
            <option value="complaint" data-id="element-5829">Жалоба</option>
            <option value="refund" data-id="element-5830">Возврат</option>
            <option value="tech" data-id="element-5831">Техническая</option>
            <option value="ai" data-id="element-5832">ИИ-отчёт</option>
          </select>
          <button onClick={() => {
        setFilters({
          status: 'all',
          priority: 'all',
          type: 'all'
        });
        setMetricFilter(null);
      }} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium" data-id="element-5833">
            Сбросить
          </button>
        </div>}

      {/* Main 3-Column Layout */}
      <div className="flex-1 flex gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white" data-id="element-5834">
        {/* LEFT: Ticket List */}
        <div className={`w-80 xl:w-96 flex-shrink-0 flex flex-col border-r border-slate-200 bg-slate-50/50 ${isMobileChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-5835">
          <div className="p-2.5 border-b border-slate-200 bg-white flex-shrink-0" data-id="element-5836">
            <div className="relative" data-id="element-5837">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" data-id="element-5838" />
              <input type="text" placeholder="Поиск по тикетам..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50" data-id="element-5839" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-5840">
            {filteredTickets.length === 0 ? <div className="p-8 text-center text-slate-400" data-id="element-5841">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" data-id="element-5842" />
                <p className="text-sm" data-id="element-5843">Нет тикетов</p>
              </div> : filteredTickets.map(ticket => {
            const isPinned = pinnedTickets.has(ticket.id);
            const isSelected = selectedTickets.has(ticket.id);
            const flags = ticketFlags[ticket.id];
            return <div key={ticket.id} onClick={() => setActiveTicketId(ticket.id)} className={`border-b border-slate-100 cursor-pointer transition-colors ${activeTicketId === ticket.id ? 'bg-indigo-50/70 border-l-2 border-l-indigo-500' : 'hover:bg-slate-50'} ${isPinned ? 'bg-amber-50/30' : ''} ${isTerminal(ticket.status) ? 'opacity-60' : ''} ${compactMode ? 'px-3 py-2' : 'px-3 py-3'}`} data-id="element-5844">
                    <div className="flex items-start gap-2" data-id="element-5845">
                      <button onClick={e => toggleTicketSelection(ticket.id, e)} className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 hover:border-indigo-400'}`} data-id="element-5846">
                        {isSelected && <Check className="w-3 h-3" data-id="element-5847" />}
                      </button>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${ticket.type === 'complaint' ? 'bg-rose-100 text-rose-700' : ticket.type === 'refund' ? 'bg-amber-100 text-amber-700' : ticket.type === 'tech' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5848">
                        {ticket.avatar}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-5849">
                        <div className="flex items-center justify-between gap-1 mb-0.5" data-id="element-5850">
                          <div className="flex items-center gap-1.5 min-w-0" data-id="element-5851">
                            {isPinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" data-id="element-5852" />}
                            <span className="text-xs font-bold text-slate-900 truncate" data-id="element-5853">
                              {ticket.user}
                            </span>
                            {ticket.priority === 'high' && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" data-id="element-5854" />}
                          </div>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap flex-shrink-0" data-id="element-5855">
                            {ticket.date}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 mb-1" data-id="element-5856">
                          {getTypeIcon(ticket.type)}
                          <span className="text-xs font-medium text-slate-800 truncate" data-id="element-5857">
                            {ticket.subject}
                          </span>
                        </div>
                        {!compactMode && <p className="text-[11px] text-slate-500 truncate mb-1.5" data-id="element-5858">
                            {ticket.lastMessage}
                          </p>}
                        <div className="flex items-center gap-1.5 flex-wrap" data-id="element-5859">
                          {getStatusBadge(ticket.status)}
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${getSlaColor(ticket.slaStatus)}`} data-id="element-5860">
                            SLA: {ticket.slaTime}
                          </span>
                          {flags?.isEscalated && <span className="text-[9px] font-bold text-rose-600 bg-rose-50 px-1 rounded" data-id="element-5861">
                              ESC
                            </span>}
                          {flags?.hasRefund && <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1 rounded" data-id="element-5862">
                              Возврат
                            </span>}
                          {flags?.isBlocked && <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1 rounded" data-id="element-5863">
                              Блок
                            </span>}
                          {ticket.hasAttachment && <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" data-id="element-5864" />}
                          {ticket.unread > 0 && <span className="bg-indigo-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center" data-id="element-5865">
                              {ticket.unread}
                            </span>}
                        </div>
                      </div>
                    </div>
                  </div>;
          })}
          </div>
        </div>

        {/* CENTER: Chat */}
        <div className={`flex-1 flex flex-col min-w-0 ${!isMobileChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-5866">
          {!activeTicket ? <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50/30" data-id="element-5867">
              <MessageSquare className="w-12 h-12 text-slate-200 mb-3" data-id="element-5868" />
              <div className="text-sm font-medium" data-id="element-5869">Выберите тикет</div>
              <div className="text-xs text-slate-300 mt-1" data-id="element-5870">
                Нажмите на тикет слева
              </div>
            </div> : <>
              {/* Chat Header */}
              <div className="p-3 border-b border-slate-100 flex justify-between items-start flex-shrink-0 bg-white" data-id="element-5871">
                <div className="min-w-0 flex-1" data-id="element-5872">
                  <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-5873">
                    <button onClick={() => setActiveTicketId(null)} className="lg:hidden p-1 text-slate-400 hover:text-slate-600 rounded flex-shrink-0" data-id="element-5874">
                      <ArrowLeft className="w-4 h-4" data-id="element-5875" />
                    </button>
                    <span className="text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded flex-shrink-0" data-id="element-5876">
                      {activeTicket.id}
                    </span>
                    <h3 className="text-sm font-bold text-slate-900 truncate" data-id="element-5877">
                      {activeTicket.subject}
                    </h3>
                    {getStatusBadge(activeTicket.status)}
                    {activeTicket.priority === 'high' && <Badge variant="error" size="sm" data-id="element-5878">
                        Высокий
                      </Badge>}
                    {ticketFlags[activeTicket.id]?.isEscalated && <Badge variant="error" size="sm" data-id="element-5879">
                        Эскалация
                      </Badge>}
                    {ticketFlags[activeTicket.id]?.hasRefund && <Badge variant="warning" size="sm" data-id="element-5880">
                        Возврат
                      </Badge>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap" data-id="element-5881">
                    <span className="flex items-center gap-1" data-id="element-5882">
                      <User className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5883" />{' '}
                      {activeTicket.user}
                    </span>
                    <span className="flex items-center gap-1" data-id="element-5884">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5885" />{' '}
                      {activeTicket.date}
                    </span>
                    {activeTicket.assignee && <span className="flex items-center gap-1" data-id="element-5886">
                        <Shield className="w-3.5 h-3.5 flex-shrink-0" data-id="element-5887" />{' '}
                        {activeTicket.assignee}
                      </span>}
                    <span className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${getSlaColor(activeTicket.slaStatus)}`} data-id="element-5888">
                      SLA: {activeTicket.slaTime}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2" data-id="element-5889">
                  <Button variant="outline" size="sm" className="text-[10px] h-7 px-2" onClick={() => togglePin(activeTicket.id)} data-id="element-5890">
                    <Pin className={`w-3.5 h-3.5 ${pinnedTickets.has(activeTicket.id) ? 'text-amber-500' : ''}`} data-id="element-5891" />
                  </Button>
                  {!isTerminal(activeTicket.status) ? <Button variant="outline" size="sm" className="text-[10px] h-7" icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-5893" />} onClick={() => handleCloseTicket(activeTicket.id)} data-id="element-5892">
                      Закрыть
                    </Button> : <Button variant="outline" size="sm" className="text-[10px] h-7" icon={<RefreshCw className="w-3.5 h-3.5" data-id="element-5895" />} onClick={() => handleReopenTicket(activeTicket.id)} data-id="element-5894">
                      Переоткрыть
                    </Button>}
                  {activeTicket.status === 'open' && !activeTicket.assignee && <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => handleTakeTicket(activeTicket.id)} data-id="element-5896">
                      Взять
                    </Button>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isEscalated && <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => handleEscalate(activeTicket.id)} data-id="element-5897">
                        <ArrowUp className="w-3.5 h-3.5" data-id="element-5898" />
                      </Button>}
                  <button onClick={() => setShowInfoPanel(!showInfoPanel)} className={`p-1.5 rounded-lg transition-colors ${showInfoPanel ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`} data-id="element-5899">
                    <Info className="w-4 h-4" data-id="element-5900" />
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="px-3 py-2 border-b border-slate-50 bg-slate-50/50 flex gap-1.5 overflow-x-auto flex-shrink-0" data-id="element-5901">
                <button onClick={() => {
              setShowInfoPanel(true);
              setCollapsedSections(p => {
                const n = new Set(p);
                n.delete('user');
                return n;
              });
            }} className="flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-medium text-slate-600 hover:bg-slate-50 whitespace-nowrap flex-shrink-0" data-id="element-5902">
                  <User className="w-3 h-3" data-id="element-5903" /> Пользователь
                </button>
                {!isTerminal(activeTicket.status) && <>
                    {!ticketFlags[activeTicket.id]?.hasRefund && <button onClick={() => handleRefund(activeTicket.id)} className="flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-medium text-slate-600 hover:bg-slate-50 whitespace-nowrap flex-shrink-0" data-id="element-5904">
                        <CreditCard className="w-3 h-3" data-id="element-5905" /> Возврат
                      </button>}
                    {!ticketFlags[activeTicket.id]?.isBlocked && <button onClick={() => setConfirmAction({
                label: `Блокировка ${activeTicket.user}`,
                action: () => handleBlockUser(activeTicket.id, activeTicket.user)
              })} className="flex items-center gap-1 px-2 py-1 bg-white border border-rose-200 rounded-lg text-[10px] font-medium text-rose-600 hover:bg-rose-50 whitespace-nowrap flex-shrink-0" data-id="element-5906">
                        <Shield className="w-3 h-3" data-id="element-5907" /> Блокировка
                      </button>}
                  </>}
                {ticketFlags[activeTicket.id]?.hasRefund && <span className="flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded-lg text-[10px] font-medium text-purple-600 whitespace-nowrap flex-shrink-0" data-id="element-5908">
                    <CheckCircle2 className="w-3 h-3" data-id="element-5909" /> Возврат оформлен
                  </span>}
                {ticketFlags[activeTicket.id]?.isBlocked && <span className="flex items-center gap-1 px-2 py-1 bg-rose-50 border border-rose-200 rounded-lg text-[10px] font-medium text-rose-600 whitespace-nowrap flex-shrink-0" data-id="element-5910">
                    <Shield className="w-3 h-3" data-id="element-5911" /> Заблокирован
                  </span>}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30" data-id="element-5912">
                {allMessages.map(msg => {
              if (msg.sender === 'system') {
                return <div key={msg.id} className="flex justify-center" data-id="element-5913">
                        <div className="bg-slate-100 text-slate-500 text-[10px] px-3 py-1.5 rounded-full flex items-center gap-1.5 max-w-[90%]" data-id="element-5914">
                          <History className="w-3 h-3 flex-shrink-0" data-id="element-5915" />
                          <span className="truncate" data-id="element-5916">{msg.text}</span>
                          <span className="text-slate-400 ml-1 flex-shrink-0" data-id="element-5917">
                            {msg.time}
                          </span>
                        </div>
                      </div>;
              }
              if (msg.sender === 'note') {
                return <div key={msg.id} className="max-w-[85%] mx-auto" data-id="element-5918">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-id="element-5919">
                          <div className="flex items-center gap-1.5 mb-1" data-id="element-5920">
                            <Lock className="w-3 h-3 text-amber-600 flex-shrink-0" data-id="element-5921" />
                            <span className="text-[10px] font-bold text-amber-700" data-id="element-5922">
                              Внутренняя заметка
                            </span>
                            <span className="text-[9px] text-amber-500 ml-auto flex-shrink-0" data-id="element-5923">
                              {msg.time}
                            </span>
                          </div>
                          <p className="text-xs text-amber-800 break-words" data-id="element-5924">
                            {msg.text}
                          </p>
                        </div>
                      </div>;
              }
              const isOwn = msg.sender === 'admin';
              return <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`} data-id="element-5925">
                      <div className="flex items-end gap-2 max-w-[80%]" data-id="element-5926">
                        {!isOwn && <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${msg.sender === 'astrologer' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5927">
                            {msg.sender === 'astrologer' ? 'АС' : activeTicket.avatar}
                          </div>}
                        <div data-id="element-5928">
                          <div className={`px-3 py-2 rounded-2xl ${isOwn ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'}`} data-id="element-5929">
                            <p className="text-xs break-words" data-id="element-5930">{msg.text}</p>
                            {msg.attachments && msg.attachments.length > 0 && <div className={`flex items-center gap-1.5 mt-1.5 pt-1.5 border-t ${isOwn ? 'border-indigo-500' : 'border-slate-100'}`} data-id="element-5931">
                                <Paperclip className={`w-3 h-3 flex-shrink-0 ${isOwn ? 'text-indigo-300' : 'text-slate-400'}`} data-id="element-5932" />
                                <span className={`text-[10px] truncate ${isOwn ? 'text-indigo-200' : 'text-indigo-600'}`} data-id="element-5933">
                                  {msg.attachments[0]}
                                </span>
                              </div>}
                          </div>
                          <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end mr-1' : 'ml-1'}`} data-id="element-5934">
                            <span className="text-[9px] text-slate-400" data-id="element-5935">
                              {msg.time}
                            </span>
                            {isOwn && msg.isRead && <CheckCheck className="w-3 h-3 text-indigo-400" data-id="element-5936" />}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
                <div ref={chatEndRef} data-id="element-5937" />
              </div>

              {/* Input */}
              {!isTerminal(activeTicket.status) && <div className="border-t border-slate-100 bg-white flex-shrink-0" data-id="element-5938">
                  <div className="px-3 pt-2 flex gap-1.5 overflow-x-auto" data-id="element-5939">
                    {quickTemplates.map(t => <button key={t.label} onClick={() => setMessageText(t.text)} className="px-2 py-1 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 text-[10px] font-medium rounded-lg whitespace-nowrap transition-colors flex-shrink-0" data-id="element-5940">
                        {t.label}
                      </button>)}
                  </div>
                  <div className="p-3 flex items-end gap-2" data-id="element-5941">
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach} data-id="element-5942" />
                    <button onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0" data-id="element-5943">
                      <Paperclip className="w-4 h-4" data-id="element-5944" />
                    </button>
                    <button onClick={() => setIsInternalNote(!isInternalNote)} className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isInternalNote ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:text-amber-600 hover:bg-slate-100'}`} title="Внутренняя заметка" data-id="element-5945">
                      <Lock className="w-4 h-4" data-id="element-5946" />
                    </button>
                    <div className="flex-1 min-w-0" data-id="element-5947">
                      <textarea value={messageText} onChange={e => setMessageText(e.target.value)} placeholder={isInternalNote ? 'Внутренняя заметка (не видна клиенту)...' : 'Ответ пользователю...'} className={`w-full rounded-xl border p-2.5 text-xs resize-none min-h-[40px] max-h-[120px] focus:outline-none focus:ring-1 ${isInternalNote ? 'border-amber-300 bg-amber-50/50 focus:border-amber-500 focus:ring-amber-500' : 'border-slate-200 bg-slate-50 focus:border-indigo-500 focus:ring-indigo-500'}`} rows={1} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }} data-id="element-5948" />
                    </div>
                    <Button variant="primary" className="h-10 w-10 rounded-xl flex items-center justify-center p-0 flex-shrink-0" disabled={!messageText.trim()} onClick={handleSendMessage} data-id="element-5949">
                      <Send className="w-4 h-4" data-id="element-5950" />
                    </Button>
                  </div>
                </div>}
            </>}
        </div>

        {/* RIGHT: Info Panel */}
        {showInfoPanel && activeTicket && <div className="w-72 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto hidden xl:block" data-id="element-5951">
            <div className="p-4 border-b border-slate-100" data-id="element-5952">
              <button onClick={() => toggleSection('user')} className="flex items-center justify-between w-full mb-3" data-id="element-5953">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-5954">
                  Пользователь
                </h4>
                {collapsedSections.has('user') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-5955" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-5956" />}
              </button>
              {!collapsedSections.has('user') && <div className="space-y-2.5" data-id="element-5957">
                  <div className="flex items-center gap-3" data-id="element-5958">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${activeTicket.type === 'complaint' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-5959">
                      {activeTicket.avatar}
                    </div>
                    <div className="min-w-0" data-id="element-5960">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-5961">
                        {activeTicket.user}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate" data-id="element-5962">
                        {activeTicket.userProfile.email}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2" data-id="element-5963">
                    <div className="text-center p-2 bg-slate-50 rounded-lg" data-id="element-5964">
                      <div className="text-sm font-bold text-slate-900" data-id="element-5965">
                        {activeTicket.userProfile.totalOrders}
                      </div>
                      <div className="text-[9px] text-slate-500" data-id="element-5966">Заказов</div>
                    </div>
                    <div className="text-center p-2 bg-slate-50 rounded-lg" data-id="element-5967">
                      <div className="text-sm font-bold text-slate-900 truncate" data-id="element-5968">
                        {activeTicket.userProfile.ltv}
                      </div>
                      <div className="text-[9px] text-slate-500" data-id="element-5969">LTV</div>
                    </div>
                  </div>
                  <div className="space-y-1.5" data-id="element-5970">
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-5971">
                      <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5972" />
                      <span className="truncate" data-id="element-5973">
                        {activeTicket.userProfile.phone}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-5974">
                      <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5975" />
                      <span className="truncate" data-id="element-5976">
                        {activeTicket.userProfile.email}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-id="element-5977">
                      <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5978" />
                      Рег: {activeTicket.userProfile.regDate}
                    </div>
                    {activeTicket.userProfile.activeSubscription && <div className="flex items-center gap-2 text-xs" data-id="element-5979">
                        <Tag className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" data-id="element-5980" />
                        <span className="text-indigo-600 font-medium" data-id="element-5981">
                          {activeTicket.userProfile.activeSubscription}
                        </span>
                      </div>}
                  </div>
                </div>}
            </div>

            <div className="p-4 border-b border-slate-100" data-id="element-5982">
              <button onClick={() => toggleSection('risk')} className="flex items-center justify-between w-full mb-3" data-id="element-5983">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-5984">
                  Риск и SLA
                </h4>
                {collapsedSections.has('risk') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-5985" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-5986" />}
              </button>
              {!collapsedSections.has('risk') && <div className="space-y-2" data-id="element-5987">
                  <div className="flex items-center justify-between" data-id="element-5988">
                    <span className="text-xs text-slate-500" data-id="element-5989">
                      Уровень риска
                    </span>
                    {getRiskBadge(activeTicket.riskLevel)}
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5990">
                    <span className="text-xs text-slate-500" data-id="element-5991">SLA статус</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${getSlaColor(activeTicket.slaStatus)}`} data-id="element-5992">
                      {activeTicket.slaTime}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5993">
                    <span className="text-xs text-slate-500" data-id="element-5994">Тип</span>
                    <div className="flex items-center gap-1" data-id="element-5995">
                      {getTypeIcon(activeTicket.type)}
                      <span className="text-xs text-slate-700" data-id="element-5996">
                        {activeTicket.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5997">
                    <span className="text-xs text-slate-500" data-id="element-5998">
                      Ответственный
                    </span>
                    <span className="text-xs text-slate-700" data-id="element-5999">
                      {activeTicket.assignee || '—'}
                    </span>
                  </div>
                </div>}
            </div>

            <div className="p-4" data-id="element-6000">
              <button onClick={() => toggleSection('actions')} className="flex items-center justify-between w-full mb-3" data-id="element-6001">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider" data-id="element-6002">
                  Действия
                </h4>
                {collapsedSections.has('actions') ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" data-id="element-6003" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" data-id="element-6004" />}
              </button>
              {!collapsedSections.has('actions') && <div className="space-y-1.5" data-id="element-6005">
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.hasRefund && <button onClick={() => handleRefund(activeTicket.id)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-700 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-6006">
                        <CreditCard className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-6007" />{' '}
                        Оформить возврат
                      </button>}
                  {ticketFlags[activeTicket.id]?.hasRefund && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-purple-600 bg-purple-50 rounded-lg" data-id="element-6008">
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6009" />{' '}
                      Возврат оформлен
                    </div>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isEscalated && <button onClick={() => handleEscalate(activeTicket.id)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-orange-700 hover:bg-orange-50 rounded-lg transition-colors" data-id="element-6010">
                        <ArrowUp className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" data-id="element-6011" />{' '}
                        Эскалировать
                      </button>}
                  {ticketFlags[activeTicket.id]?.isEscalated && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-600 bg-rose-50 rounded-lg" data-id="element-6012">
                      <ArrowUp className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6013" />{' '}
                      Эскалирован
                    </div>}
                  {!isTerminal(activeTicket.status) && !ticketFlags[activeTicket.id]?.isBlocked && <button onClick={() => setConfirmAction({
              label: `Блокировка ${activeTicket.user}`,
              action: () => handleBlockUser(activeTicket.id, activeTicket.user)
            })} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-6014">
                        <Shield className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" data-id="element-6015" />{' '}
                        Заблокировать
                      </button>}
                  {ticketFlags[activeTicket.id]?.isBlocked && <div className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-600 bg-slate-100 rounded-lg" data-id="element-6016">
                      <Shield className="w-3.5 h-3.5 flex-shrink-0" data-id="element-6017" />{' '}
                      Заблокирован
                    </div>}
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}